import { Link } from "react-router-dom";
import { FileText } from "lucide-react";
import Navbar from "@/components/Navbar";
import { Card } from "@/components/ui/card";
import Seo from "@/components/Seo";

const Terms = () => {
  return (
    <div className="min-h-screen bg-background">
      <Seo
        title="Conditions générales d'utilisation"
        description="Conditions générales d'utilisation de Click&Gard : réservations, paiements, obligations des parents et professionnels, responsabilité."
      />
      <Navbar />
      <main className="w-full px-4 md:px-6 lg:px-8 py-16">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12 animate-fade-in">
            <div className="inline-flex w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-secondary items-center justify-center shadow-neon mb-5">
              <FileText className="w-8 h-8 text-primary-foreground" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold font-display tracking-tight mb-4">
              Conditions générales <span className="text-gradient-static">d'utilisation</span>
            </h1>
            <p className="text-sm text-muted-foreground">Dernière mise à jour : 20 avril 2026</p>
          </div>

          <Card className="card-glass-premium p-8 md:p-10 rounded-3xl space-y-8 text-foreground">
            <section>
              <h2 className="text-xl font-bold mb-3 text-primary">1. Objet</h2>
              <p className="text-muted-foreground leading-relaxed">
                Les présentes Conditions Générales d'Utilisation (CGU) ont pour objet de définir
                les modalités et conditions d'utilisation de la plateforme <strong className="text-foreground">Click&Gard</strong>,
                qui met en relation des parents avec des professionnels de la garde d'enfants.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold mb-3 text-primary">2. Acceptation des CGU</h2>
              <p className="text-muted-foreground leading-relaxed">
                L'utilisation de Click&Gard implique l'acceptation pleine et entière des présentes CGU.
                Tout utilisateur s'inscrivant sur la plateforme reconnaît avoir lu, compris et accepté ces conditions.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold mb-3 text-primary">3. Inscription et compte utilisateur</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                L'inscription est gratuite pour les parents. Les professionnels doivent souscrire à un abonnement
                pour accéder à l'ensemble des fonctionnalités (messagerie, réservations, annonces).
              </p>
              <p className="text-muted-foreground leading-relaxed">
                L'utilisateur s'engage à fournir des informations exactes et à les maintenir à jour.
                Il est seul responsable de la confidentialité de ses identifiants de connexion.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold mb-3 text-primary">4. Vérification des professionnels</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Les professionnels font l'objet d'une vérification d'identité (pièce d'identité + selfie)
                réalisée manuellement par notre équipe. Cette vérification ne constitue pas une garantie absolue
                de la qualité des prestations fournies.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                <strong className="text-foreground">Traçabilité des connexions</strong> : pour garantir la sécurité
                de la plateforme et prévenir la fraude, le professionnel reconnaît et accepte que ses
                connexions (inscription et chaque login) soient enregistrées avec son adresse IP, sa localisation
                approximative (ville, région, pays) et le type d'appareil/navigateur utilisé. Ces informations
                sont conservées pendant 12 mois maximum et accessibles uniquement aux administrateurs Click&Gard.
                Plus de détails dans notre{" "}
                <Link to="/privacy" className="text-primary hover:underline">Politique de confidentialité</Link>.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold mb-3 text-primary">5. Réservations et paiements</h2>
              <p className="text-muted-foreground leading-relaxed">
                Les réservations s'effectuent directement entre parents et professionnels via la plateforme.
                Les paiements des prestations de garde sont gérés directement entre les parties.
                Click&Gard perçoit uniquement les abonnements professionnels via Stripe.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold mb-3 text-primary">6. Obligations des utilisateurs</h2>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground leading-relaxed">
                <li>Respecter les autres utilisateurs et les règles de bienséance</li>
                <li>Ne pas publier de contenu illégal, diffamatoire ou inapproprié</li>
                <li>Ne pas utiliser la plateforme à des fins frauduleuses</li>
                <li>Ne pas tenter de contourner le système d'abonnement</li>
                <li>Respecter la législation en vigueur en matière de garde d'enfants</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold mb-3 text-primary">7. Responsabilité</h2>
              <p className="text-muted-foreground leading-relaxed">
                Click&Gard agit uniquement en tant qu'intermédiaire technique. La plateforme ne saurait être tenue
                responsable des prestations fournies par les professionnels, ni des litiges pouvant survenir
                entre parents et professionnels.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold mb-3 text-primary">8. Suspension et résiliation</h2>
              <p className="text-muted-foreground leading-relaxed">
                Click&Gard se réserve le droit de suspendre ou supprimer tout compte ne respectant pas
                les présentes CGU, sans préavis ni indemnité. L'utilisateur peut à tout moment supprimer
                son compte depuis l'espace Paramètres.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold mb-3 text-primary">9. Modification des CGU</h2>
              <p className="text-muted-foreground leading-relaxed">
                Click&Gard se réserve le droit de modifier les présentes CGU à tout moment.
                Les utilisateurs seront informés des modifications substantielles par e-mail
                ou via une notification sur la plateforme.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold mb-3 text-primary">10. Droit applicable et litiges</h2>
              <p className="text-muted-foreground leading-relaxed">
                Les présentes CGU sont régies par le droit français. En cas de litige, une solution amiable
                sera recherchée avant toute action judiciaire. À défaut, les tribunaux français seront seuls compétents.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold mb-3 text-primary">11. Contact</h2>
              <p className="text-muted-foreground leading-relaxed">
                Pour toute question relative aux présentes CGU, vous pouvez nous contacter à :{" "}
                <a href="mailto:contact@clickandgard.fr" className="text-primary hover:underline">
                  contact@clickandgard.fr
                </a>.
              </p>
            </section>
          </Card>

          <div className="mt-8 text-center">
            <Link to="/" className="text-sm text-muted-foreground hover:text-primary transition-colors">
              ← Retour à l'accueil
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Terms;
